'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Plus, Star } from 'lucide-react';
import React from 'react';
import TrendingIcon from '@/../public/static/icons/trending-icon.svg';
import type { Item } from '@/api/use-get-personalize';
import { useNavigation } from '@/components/navigation/navigation-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuth } from '@/providers/auth-provider';

interface PersonalizeItemProps {
  item: Item;
  isCenter: boolean;
  index: number;
  handleSuggestionClick?: (suggestion: string) => void;
  handleItemClick: (item: Item) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function PersonalizeItem({
  item,
  isCenter,
  index,
  handleItemClick,
}: PersonalizeItemProps) {
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuth();
  const { setForceHidden } = useNavigation();

  const handleCardClick = () => {
    if (item.slug) {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'for_you_card_click',
        button_location: 'for_you_section',
        storeId: item.store_id,
        item_name: item.name,
        item_id: item._id,
        position: index + 1,
        user_id: user?.cunstomerId,
      });
      setForceHidden(false);
      router.push(routes.storeDetail(item.slug));
      if (
        typeof window !== 'undefined' &&
        typeof window.setOpenChange === 'function'
      ) {
        window.setOpenChange(false);
      }
    }
  };

  let rawRating: number | null = null;
  if (item.rating?.average && item.rating.average > 0) {
    rawRating = item.rating.average;
  } else if (
    item.rating?.google_average &&
    item.rating.google_average >= 4 &&
    item.rating.total === 0
  ) {
    rawRating = item.rating.google_average;
  }
  const showRating = rawRating !== null;
  const showSold = item.total_sold > 10;
  let soldDisplay = '';
  if (showSold) {
    soldDisplay =
      item.total_sold > 999
        ? `${(item.total_sold / 1000).toFixed(1)}K`
        : `${item.total_sold}`;
  }

  const isSoloMeal = item.extra_tags.includes('SOLO_MEAL');

  return (
    <Card
      onClick={handleCardClick}
      key={item._id}
      className={cn(
        'w-[170px] shrink-0 cursor-pointer relative overflow-hidden block p-0 transition-all',
        // Liquid glass (iOS 26 subtle) — bg gradient + backdrop-blur + border + top inner shine
        'bg-gradient-to-b from-white/70 via-white/50 to-white/30',
        'backdrop-blur-sm',
        'border border-white/60',
        'rounded-2xl',
        'shadow-[0_4px_16px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.6)]',
        isCenter
          ? 'ring-2 ring-primary-30/60'
          : 'hover:shadow-[0_6px_20px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.6)]'
      )}
    >
      {/* Image — square top, dominant */}
      <div className="relative aspect-square w-full bg-neutral-10">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            src={item.image_thumb_url || '/static/images/empty-store-bot.webp'}
            alt={item.name}
            className="size-full object-cover"
          />
          <AvatarFallback className="size-full rounded-none">
            {item.name?.charAt(0) ?? 'M'}
          </AvatarFallback>
        </Avatar>

        {/* SoloMeal badge — small pill (not big banner) */}
        {isSoloMeal ? (
          <div className="absolute top-1.5 left-1.5 z-20 px-1.5 py-0 bg-gradient-to-r from-[#FFEAA8] to-[#FDC9B9] flex items-center rounded-md shadow-sm backdrop-blur-sm">
            <span className="text-[9px] font-black text-primary-60 uppercase tracking-wider">
              Solo Meal
            </span>
          </div>
        ) : null}

        {/* AI badge */}
        {item.is_ai_image ? (
          <div className="absolute top-1.5 right-1.5 z-20 size-5 bg-white/80 backdrop-blur-sm rounded-md shadow-sm flex items-center justify-center">
            <Image
              src={`${assetPrefix}/static/icons/white-ai.svg`}
              alt="AI"
              width={12}
              height={12}
              className="size-3"
            />
          </div>
        ) : null}

        {/* ETA badge với badge-tail (giữ AhaFood specific) */}
        <div className="absolute bottom-0 left-0 flex items-end gap-0">
          <div className="flex items-center h-5 px-1.5 bg-white rounded-tr-2xl">
            <img
              src={`${assetPrefix}/static/icons/clock.svg`}
              alt="ETA"
              className="mr-1 size-3"
            />
            <span className="text-[10px] font-semibold text-neutral-60">
              Từ {item.eta} phút
            </span>
          </div>
          <img
            src={`${assetPrefix}/static/icons/badge-tail.svg`}
            alt=""
            className="size-4 object-contain pointer-events-none -mb-0.5 -ml-0.5"
          />
        </div>

        {/* Add button bottom-right — liquid glass white circle */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleItemClick(item);
          }}
          size="icon"
          variant="ghost"
          className="absolute bottom-1.5 right-1.5 z-20 size-6 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white text-primary-50 shadow-md border border-white/60"
        >
          <Plus className="size-4" />
        </Button>
      </div>

      {/* Content — compact stack */}
      <div className="px-2 pt-1.5 pb-2 flex flex-col gap-1">
        <h3 className="font-medium text-xs text-neutral-90 leading-tight line-clamp-2 min-h-8">
          {item.trend_item ? (
            <TrendingIcon className="inline-block mr-1 animate-fire-pulse" />
          ) : null}
          {item.name}
        </h3>

        {/* Price highlight — yellow bg như SoloMeal */}
        <div className="flex items-baseline gap-1.5 flex-wrap">
          {item.discount_price ? (
            <>
              <span className="text-sm font-bold bg-[#FEBC2F] text-neutral-90 px-1 rounded-sm">
                {item.discount_price.toLocaleString()}đ
              </span>
              <span className="text-[10px] line-through text-neutral-50 font-medium">
                {item.price.toLocaleString()}đ
              </span>
            </>
          ) : (
            <span className="text-sm font-bold bg-[#FEBC2F] text-neutral-90 px-1 rounded-sm">
              {item.price.toLocaleString()}đ
            </span>
          )}
        </div>

        {/* Store + distance */}
        <div className="text-[11px] text-neutral-60 flex justify-between gap-1">
          <span className="truncate flex-1">{item.page_name}</span>
          <span className="shrink-0 font-medium">
            {item.distance.toFixed(1)}km
          </span>
        </div>

        {/* Rating + sold — inline plain text */}
        {showRating || showSold ? (
          <div className="flex items-center gap-1 text-[10px] text-neutral-60">
            {showRating && (
              <div className="flex items-center gap-0.5">
                {item.rating?.total === 0 ? (
                  <img
                    src="/static/images/gg-rating.webp"
                    alt="Store"
                    className="inline-block h-auto w-3"
                  />
                ) : (
                  <Star className="size-3 fill-[#FEBC2F] text-[#FEBC2F]" />
                )}
                <span className="font-semibold">{rawRating!.toFixed(1)}</span>
              </div>
            )}
            {showRating && showSold ? (
              <span className="opacity-50">|</span>
            ) : null}
            {showSold ? <span>{soldDisplay} lượt bán</span> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
